import { companyZone } from "../../ui/zone";
import type {
  AuthorizationView,
  CarePlanTab,
  PatientAuthorizations,
} from "../../../server/modules/homecare/careplans";
import { HOLD_END_LABEL, HOLD_ENDS } from "../../../server/modules/homecare/rules/careplans";
import {
  DAY_NAMES,
  EVERY_VISIT,
  OFTEN,
  OFTEN_LABEL,
  PART_OF_DAY_LABEL,
  PARTS_OF_DAY,
  readTaskSchedule,
  taskLabel,
  WINDOW_LABEL,
  WINDOWS,
  clockWords,
  type TaskSchedule,
} from "../../../server/modules/homecare/rules/care-tasks";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, muted, table } from "../../ui/record";
import { uploadControl } from "../../ui/upload";
import { openViewer } from "../../ui/viewer";
import { area, field, refuse, select } from "../clients/forms";
import { DATE_EXAMPLE, DATE_REFUSED, dayOf } from "../../../server/rules/dates";
import { BILL_BY, BILL_BY_LABEL } from "../../../server/rules/billing-rules";

// The patient's Care plan tab (brief 1C-3, mockup planView, holdBanner, holdModal, resumeModal and
// editLineModal), the Authorizations card on Patient and payer (mockup authrow), and the hold
// banner with its Resume care choice. The plan: drafted from the signed assessment, its lines
// changed while it's a draft, signed by an RN, then never changed in place (Change adds the new
// line and archives the old one, with why). Services with Who, Level, What and how often, Billed
// by (a title that doesn't fit the level is a note, never a block; no authorization: brief 1E-25);
// Tasks; Precautions; Holds and extras; the Nursing schedule (Reassessment, Recertification, PRN,
// aide supervision, editable with a reason). Physician orders moved to the Next steps card on Patient and payer
// (brief 1C-4, nextsteps.ts). Small actions are icon buttons with hover tips.

type Tab = CarePlanTab;
type Res = {
  ok: boolean;
  status: number;
  body: Tab & {
    error?: string;
    moved?: string | null;
    file?: { id: string; name: string };
    how?: string;
    episodeNumber?: number;
  };
};
type Plan = NonNullable<Tab["plan"]>;
type ServiceLine = Plan["services"][number];
type TaskLine = Plan["tasks"][number];

const kv = (rows: [string, Node | string][]) =>
  el("dl", { class: "kv" }, ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]));

/** A small button with an icon and words (the mockup's "btn sm"). */
export function btn(
  words: string,
  iconName: string,
  run: () => void,
  cls = "btn sm",
): HTMLButtonElement {
  const b = el("button", { type: "button", class: cls }, icon(iconName), ` ${words}`);
  b.addEventListener("click", run);
  return b;
}

export const go = (words: string, run: () => Promise<unknown>, cls = "btn pri") => {
  const b = el("button", { type: "button", class: cls }, words);
  b.addEventListener("click", async () => {
    b.disabled = true;
    await run();
    b.disabled = false;
  });
  return b;
};

export const form = (...parts: (Node | null)[]) =>
  el("div", { class: "form", style: "max-width:none" }, ...parts);

let sheetIds = 0;

/**
 * The care task sheet's two choice groups (brief 1E-10, mockup taskModal): when in the day (the
 * company's parts of the day with their hours, or a set time with a window) and how often (day
 * chips, the times a week box), with the line the aide will see.
 */
function scheduleSheet(d: Tab, had: TaskSchedule) {
  const n = ++sheetIds;
  const radio = (group: string, value: string, label: string, on: boolean, sub?: string) => {
    const input = el("input", { type: "radio", name: `${group}-${n}`, value, checked: on });
    return {
      input,
      el: el(
        "label",
        { class: "tsr" },
        input,
        ` ${label}`,
        sub ? el("span", { class: "small muted" }, ` ${sub}`) : "",
      ),
    };
  };
  const hours = (k: string) => d.partsOfDay.find((p) => p.key === k)?.hours;
  const whens = PARTS_OF_DAY.map((k) =>
    radio("ts-when", k, PART_OF_DAY_LABEL[k], had.partOfDay === k, hours(k)),
  );
  const at = field("Time", had.atTime ? clockWords(had.atTime) : "9:00 AM", { maxlength: "8" });
  const win = select(
    "Window",
    WINDOWS.map((w) => [String(w), WINDOW_LABEL[w] ?? `${w} min`] as const),
    String(had.windowMinutes ?? 30),
  );
  const timeRow = el("div", { class: "grid2 tstime" }, at.el, win.el);
  const oftens = OFTEN.map((k) => radio("ts-often", k, OFTEN_LABEL[k], had.often === k));
  const dayBoxes = DAY_NAMES.map((name, i) => {
    const input = el("input", {
      type: "checkbox",
      value: String(i),
      checked: had.days.includes(i),
    });
    return { input, el: el("label", { class: "chip plain tsday" }, input, name) };
  });
  const dayRow = el(
    "div",
    { class: "tsdays", role: "group", "aria-label": "Days" },
    ...dayBoxes.map((x) => x.el),
  );
  const times = field("Times a week", String(had.timesPerWeek ?? 3), {
    type: "number",
    min: "1",
    max: "7",
  });
  const timesBox = el(
    "div",
    { class: "tsn" },
    times.el,
    el("span", { class: "small muted" }, "Counted each week. Flagged when behind."),
  );
  const preview = el("span", {});
  const raw = () => ({
    partOfDay: whens.find((w) => w.input.checked)?.input.value ?? "any",
    atTime: at.input.value,
    windowMinutes: Number(win.input.value),
    often: oftens.find((o) => o.input.checked)?.input.value ?? "every_visit",
    days: dayBoxes.filter((x) => x.input.checked).map((x) => Number(x.input.value)),
    timesPerWeek: Number(times.input.value),
  });
  const show = () => {
    const r = raw();
    timeRow.hidden = r.partOfDay !== "at_time";
    dayRow.hidden = r.often !== "days" && r.often !== "weekly";
    timesBox.hidden = r.often !== "times_week";
    for (const x of dayBoxes) x.el.className = `chip ${x.input.checked ? "info" : "plain"} tsday`;
    const read = readTaskSchedule(r);
    preview.textContent = read.ok ? taskLabel(read.value) : read.error;
  };
  const fs = (legend: string, ...kids: Node[]) =>
    el("fieldset", { class: "tsf" }, el("legend", {}, legend), ...kids);
  const parts = [
    fs("When in the day", el("div", { class: "tsgrid" }, ...whens.map((w) => w.el)), timeRow),
    fs("How often", el("div", { class: "tsgrid" }, ...oftens.map((o) => o.el)), dayRow, timesBox),
    el(
      "p",
      { class: "small", style: "margin:0" },
      el("b", {}, "On the aide's checklist: "),
      preview,
    ),
  ];
  for (const p of parts) {
    p.addEventListener("change", show);
    p.addEventListener("input", show);
  }
  show();
  return { parts, read: raw };
}

/** One of the patient's files, picked or uploaded (consents-style), for a letter or an order. */
export function filePick(
  label: string,
  files: readonly { id: string; name: string }[],
  patientId: string,
  upLabel: string,
  o: { value?: string | null; none?: string; fileKind?: string | null } = {},
) {
  const pick = select(
    label,
    [["", o.none ?? "Pick a file"], ...files.map((f) => [f.id, f.name] as const)],
    o.value ?? "",
  );
  const up = uploadControl({
    kind: "patient",
    id: patientId,
    label: upLabel,
    camera: true,
    // Filed as what it is where it's uploaded (brief 1E-23): an authorization's letter.
    fileKind: o.fileKind ?? undefined,
    onUploaded: (f) => {
      pick.input.append(el("option", { value: f.id }, f.name));
      pick.input.value = f.id;
    },
  });
  return { el: el("div", { class: "wide" }, pick.el, up.el), input: pick.input };
}

// ---- The hold banner and Resume care (on every tab of the record) ----------------------------------

/** "ON HOLD since 10/21 · In the hospital" with what's paused and Resume care (mockup holdBanner). */
export function holdBanner(
  hold: { reason: string; since: string; paused: string[] },
  patientId: string,
  canEdit: boolean,
  done: () => void,
): HTMLElement {
  const resume = canEdit
    ? btn("Resume care", "play", () => openResume(patientId, hold.since, done), "btn pri")
    : null;
  return el(
    "div",
    { class: "holdbar", role: "status", "aria-label": "On hold" },
    icon("stop"),
    el(
      "div",
      {},
      el("b", {}, `ON HOLD since ${hold.since} · ${hold.reason}`),
      el(
        "span",
        {},
        `Paused: ${hold.paused.length ? hold.paused.join(", ") : "All services"}. Nothing is billed while on hold.`,
      ),
    ),
    resume,
  );
}

/** Resume care: how the patient is coming back (mockup resumeModal). */
export function openResume(patientId: string, since: string, done: () => void) {
  const name = `rs${Math.random().toString(36).slice(2, 7)}`;
  const cards = HOLD_ENDS.map((k, i) => {
    const r = el("input", { type: "radio", name, value: k, checked: i === 1 });
    return el(
      "label",
      { class: "rcard" },
      r,
      el("span", {}, el("b", {}, HOLD_END_LABEL[k].label), el("small", {}, HOLD_END_LABEL[k].sub)),
    );
  });
  const today = new Date().toLocaleDateString("en-CA", { timeZone: companyZone() });
  const back = field("Back home on", today, { type: "date" });
  const save = go("Resume", async () => {
    const how = (m.root.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement | null)
      ?.value;
    const res = (await api("POST", `/api/patients/${patientId}/resume`, {
      how,
      backOn: back.input.value,
    })) as Res;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    toast(
      "Care resumed",
      how === "new_episode"
        ? `Episode ${res.body.episodeNumber ?? ""} started · a new assessment and plan`
        : how === "resumption"
          ? "A new assessment is due · check new medications first"
          : "Visits go back on the schedule",
    );
    done();
  });
  const m = openModal({
    title: "Resume care",
    sub: `On hold since ${since}`,
    body: [
      form(
        el(
          "fieldset",
          { class: "field" },
          el("legend", {}, "How is the patient coming back?"),
          el("div", { class: "rpick" }, ...cards),
        ),
        back.el,
      ),
    ],
    actions: [save],
  });
}

// ---- The Care plan tab -----------------------------------------------------------------------------------

/**
 * Draws the Care plan tab into `host`. `changed` tells the record to read itself again (the stage,
 * the strip and the hold banner move); `goTab` opens another tab of the record. Answers a reload.
 */
export async function carePlanTab(
  host: HTMLElement,
  ctx: ScreenContext,
  patientId: string,
  changed: () => void,
  goTab: (tab: "assess" | "visits") => void,
): Promise<{ reload: () => Promise<void> }> {
  void ctx;
  const base = `/api/patients/${encodeURIComponent(patientId)}`;
  let data: Tab | null = null;
  let showArchived = false;
  // Services and Care tasks show their first 5 lines until Show all (brief 1E-10).
  const openLists = new Set<string>();
  const FOLD = 5;
  function folded<T>(key: string, rows: readonly T[], make: (rows: readonly T[]) => HTMLElement) {
    const open = openLists.has(key);
    const shown = open || rows.length <= FOLD ? rows : rows.slice(0, FOLD);
    return el(
      "div",
      {},
      make(shown),
      rows.length > FOLD
        ? (() => {
            const b = el(
              "button",
              { type: "button", class: "btn sm ghost", style: "margin-top:4px" },
              open ? "Show fewer" : `Show all ${rows.length}`,
            );
            b.addEventListener("click", () => {
              if (open) openLists.delete(key);
              else openLists.add(key);
              draw();
            });
            return b;
          })()
        : null,
    );
  }

  async function took(res: Res, done?: string, sub?: string): Promise<Res> {
    if (res.ok) {
      data = res.body;
      draw();
      if (done)
        toast(
          done,
          sub ?? (res.body.moved ? `The episode moved on to ${res.body.moved}` : undefined),
        );
      changed();
    } else toast("That wasn't saved", res.body.error);
    return res;
  }
  const send = async (method: string, path: string, body?: unknown, done?: string, sub?: string) =>
    took((await api<Tab>(method, `${base}${path}`, body)) as Res, done, sub);

  async function reload() {
    const res = (await api<Tab>("GET", `${base}/care-plan`)) as Res;
    if (!res.ok) {
      host.replaceChildren(
        el(
          "section",
          { class: "card" },
          el("p", { class: "muted" }, res.body.error ?? "The care plan can't be opened."),
        ),
      );
      return;
    }
    data = res.body;
    draw();
  }

  // ---- Lines ----------------------------------------------------------------------------------------

  /** Add or change a line (mockup editLineModal). On a signed plan a change asks why. */
  function lineModal(kind: "service" | "task" | "precaution", line: ServiceLine | TaskLine | null) {
    const d = data as Tab;
    const plan = d.plan as Plan;
    const signed = plan.change === "new_line" && !plan.editable;
    const svc = kind === "service" ? (line as ServiceLine | null) : null;
    const task = kind === "task" ? (line as TaskLine | null) : null;
    const parts: (Node | null)[] = [];
    let read: () => Record<string, unknown> = () => ({});
    if (kind === "service") {
      const who = select(
        "Who",
        d.services.map((s) => [s.id, s.name] as const),
        svc?.serviceId ?? d.services[0]?.id ?? "",
      );
      const title = select("Title", [], null);
      const level = el("p", { class: "small muted", style: "margin:0" });
      const fill = () => {
        const s = d.services.find((x) => x.id === who.input.value);
        title.input.replaceChildren(
          ...(s?.titles.length
            ? s.titles.map((t) => el("option", { value: t, selected: t === svc?.who }, t))
            : [el("option", { value: "" }, "No title")]),
        );
        level.textContent = s
          ? `${s.name} is ${s.levelLabel}, billed by the ${s.billedLabel.toLowerCase()}. ${d.services
              .filter((x) => x.level === "non_skilled" && x.titles.length)
              .map((x) => x.name)
              .join(", ")} are non-skilled; ${d.services
              .filter((x) => x.level === "skilled")
              .map((x) => x.name)
              .join(" and ")} are skilled.`
          : "";
      };
      who.input.addEventListener("change", fill);
      fill();
      const what = field("What and how often", svc?.what ?? "", {
        placeholder: "e.g. 4 hrs a day, Monday–Friday",
        maxlength: "300",
      });
      parts.push(who.el, title.el, level, what.el);
      read = () => ({
        kind,
        serviceId: who.input.value,
        who: title.input.value || null,
        what: what.input.value,
      });
    } else if (kind === "task") {
      const what = field("Task", task?.what ?? "", {
        maxlength: "300",
        placeholder: "e.g. Shower",
      });
      const sheet = scheduleSheet(d, task?.schedule ?? EVERY_VISIT);
      parts.push(what.el, ...sheet.parts);
      read = () => ({ kind, what: what.input.value, schedule: sheet.read() });
    } else {
      const what = field("Precaution", "", { maxlength: "300" });
      parts.push(what.el);
      read = () => ({ kind, what: what.input.value });
    }
    const why = line && signed ? area("Why it changes", "") : null;
    if (why) parts.push(why.el);
    const save = go("Save", async () => {
      const res = (await api<Tab>(
        line ? "PUT" : "POST",
        line ? `${base}/care-plan/lines/${line.id}` : `${base}/care-plan/lines`,
        { ...read(), ...(why ? { why: why.input.value } : {}) },
      )) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(
        res,
        line ? "Changed" : "Added",
        line && signed ? "Old line archived today" : "Starts today",
      );
    });
    const words =
      kind === "service" ? "a service" : kind === "task" ? "a care task" : "a precaution";
    const m = openModal({
      title: line
        ? kind === "task"
          ? "Change this care task"
          : "Change this line"
        : `Add ${words}`,
      sub:
        line && signed ? "The current line is archived today and the new one starts." : undefined,
      // The care task sheet stacks its choice groups, as the mockup does.
      body: [kind === "task" ? form(...parts) : form(el("div", { class: "formgrid" }, ...parts))],
      actions: [save],
    });
  }

  async function restoreLine(id: string) {
    await send(
      "POST",
      `/care-plan/lines/${id}/restore`,
      {},
      "Restored",
      "Back on the care plan, in History",
    );
  }

  function archiveLine(id: string, what: string) {
    const why = area("Why does it stop?", "");
    const save = go("Archive", async () => {
      const res = (await api<Tab>("POST", `${base}/care-plan/lines/${id}/archive`, {
        why: why.input.value,
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(res, "Archived", "Show archived to see it");
    });
    const m = openModal({
      title: "Archive this line",
      sub: `${what} · it stops from today and moves to Archived.`,
      body: [form(why.el)],
      actions: [save],
    });
  }

  const rowBtns = (
    kind: "service" | "task" | "precaution",
    l:
      | ServiceLine
      | TaskLine
      | { id: string; what: string; archived: Plan["precautions"][number]["archived"] },
  ) => {
    const editable = (data as Tab).canEdit && !!(data as Tab).plan?.editable;
    if (l.archived)
      return el(
        "span",
        { class: "small muted" },
        `Archived ${l.archived.on} · ${l.archived.by}${l.archived.why ? ` · ${l.archived.why}` : ""}`,
        // Brief 1E-23: an archived line can be put back while the plan is a draft or edited.
        editable ? " " : "",
        editable ? btn("Restore", "history", () => restoreLine(l.id), "btn sm ghost") : "",
      );
    if (!editable) return el("span");
    return el(
      "span",
      { class: "rowbtns" },
      kind !== "precaution"
        ? ib("pencil", "Change this line", () => lineModal(kind, l as ServiceLine | TaskLine))
        : null,
      ib("archive", "Archive: stops it from today", () =>
        archiveLine(l.id, "service" in l ? l.service : l.what),
      ),
    );
  };

  // ---- Cards ----------------------------------------------------------------------------------------

  function noPlan(d: Tab): HTMLElement {
    const a = d.signedAssessment;
    return el(
      "section",
      { class: "card", "aria-label": "Care plan" },
      el(
        "div",
        { class: "blank", style: "padding:20px" },
        icon("checklist", "big"),
        el("h2", {}, "No care plan yet"),
        el(
          "p",
          { class: "muted" },
          a
            ? `Draft it from the signed assessment (${a.label}, ${a.signedOn ?? ""}).`
            : "It's drafted from the assessment once that's signed.",
        ),
        a && d.canEdit
          ? btn(
              "Draft care plan from assessment",
              "zap",
              () =>
                void send(
                  "POST",
                  "/care-plan",
                  {},
                  "Care plan drafted",
                  "From the signed assessment · check each line, then sign",
                ),
              "btn pri",
            )
          : !a
            ? btn("Go to the assessment", "arrow-right", () => goTab("assess"), "btn")
            : null,
      ),
    );
  }

  function planCard(d: Tab, p: Plan): HTMLElement {
    const hold = d.hold;
    const live = <T extends { archived: unknown }>(xs: readonly T[]) =>
      xs.filter((l) => showArchived || !l.archived);
    const nArch = [...p.services, ...p.tasks, ...p.precautions].filter((l) => l.archived).length;
    const actions: Node[] = [];
    if (d.canEdit)
      actions.push(
        hold
          ? btn(
              "Resume care",
              "play",
              () => openResume(patientId, hold.since, () => void reload().then(changed)),
              "btn sm pri",
            )
          : ib("stop", "Put on hold", () => holdModal(d, p)),
      );
    actions.push(
      ib("file", "Print the care plan", async () => {
        const res = await send("POST", "/care-plan/print", {});
        if (res.ok && res.body.file)
          void openViewer({ file: res.body.file, document: `Care plan v${p.version}` });
      }),
    );
    if (d.canEdit && p.editable)
      actions.push(ib("calendar", "Change the plan's dates", () => datesModal(p)));
    const signedLine = p.signed
      ? `Signed by ${p.signed.signature ?? `${p.signed.by}${p.signed.title ? `, ${p.signed.title}` : ""}`} · ${p.signed.at}`
      : `Draft · drafted by ${p.drafted.by} ${p.drafted.at} · not signed yet`;

    const signBox =
      p.status === "draft"
        ? el(
            "div",
            { class: `recbar ${p.canSign ? "warn" : "bad"}`, role: "status" },
            icon(p.canSign ? "clock" : "lock"),
            el(
              "span",
              {},
              el("b", {}, "Not signed yet"),
              ` · ${p.canSign ? "check each line, then sign." : (p.signRefusal ?? "")}`,
            ),
            p.canSign && d.canEdit
              ? btn(
                  "Sign care plan",
                  "check",
                  () => void send("POST", "/care-plan/sign", {}, "Care plan signed", undefined),
                  "btn sm pri",
                )
              : null,
          )
        : null;

    let summary: Node | null = null;
    if (p.editable && d.canEdit) {
      const s = area("Summary", p.summary ?? "");
      s.input.addEventListener(
        "change",
        () => void send("PUT", "/care-plan", { summary: s.input.value }, "Summary saved"),
      );
      summary = form(s.el);
    } else if (p.summary)
      summary = el(
        "p",
        { class: "small", style: "margin:8px 0" },
        el("b", {}, "Summary: "),
        p.summary,
      );

    const waiting =
      p.status === "signed" && p.waitsForOrder
        ? el(
            "div",
            { class: "recbar warn", role: "status" },
            icon("clock"),
            el(
              "span",
              {},
              el("b", {}, "Waiting for a signed physician order"),
              " · the episode is flagged until it's signed back (Patient and payer › Next steps)",
            ),
          )
        : null;

    const archToggle = nArch
      ? btn(
          `${showArchived ? "Hide" : "Show"} archived (${nArch})`,
          "archive",
          () => {
            showArchived = !showArchived;
            draw();
          },
          "btn sm ghost",
        )
      : null;

    return el(
      "section",
      { class: "card", "aria-label": "Care plan" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el(
            "div",
            { class: "eyebrow" },
            `Care plan · ${p.length} · episode ${d.episode?.number ?? ""} · v${p.version}`,
          ),
          el("h2", {}, `${p.from} – ${p.to}`),
          el("span", { class: "small muted" }, signedLine),
        ),
        el("div", { class: "actions" }, ...actions),
      ),
      signBox,
      summary,
      el(
        "div",
        { class: "cardhead", style: "margin:12px 0 6px" },
        el(
          "h3",
          { class: "h3s", style: "margin:0" },
          "Services",
          p.privatePay
            ? el(
                "span",
                { class: "small muted", style: "font-weight:400" },
                " · private pay, service agreement",
              )
            : "",
        ),
        el(
          "span",
          { class: "actions" },
          archToggle,
          d.canEdit && p.editable
            ? ib("plus", "Add service", () => lineModal("service", null))
            : null,
        ),
      ),
      folded("Services", live(p.services), (rows) =>
        table(
          [
            {
              h: "Who",
              f: (r: ServiceLine) =>
                el(
                  "span",
                  {},
                  el("b", {}, r.service),
                  r.who ? el("span", { class: "small muted" }, ` · ${r.who}`) : "",
                  r.titleNote
                    ? el("div", { class: "small muted" }, icon("info"), ` ${r.titleNote}`)
                    : "",
                ),
            },
            { h: "Level", f: (r) => chip(r.levelLabel, r.level === "skilled" ? "info" : "plain") },
            { h: "What and how often", f: (r) => r.what },
            { h: "Billed by", f: (r) => r.billedLabel },
            // A private-pay plan has no Covered by column (its service agreement covers it).
            {
              h: "Status",
              f: (r) =>
                r.archived
                  ? chip("Archived", "plain")
                  : chip(r.status, r.status === "Paused" ? "warn" : "plain"),
            },
            { h: "", f: (r) => rowBtns("service", r) },
          ],
          rows,
          undefined,
          "Services",
        ),
      ),
      waiting,
      el(
        "div",
        { class: "cardhead", style: "margin:12px 0 6px" },
        el(
          "h3",
          { class: "h3s", style: "margin:0" },
          "Care tasks",
          el("span", { class: "small muted" }, " · the aide's checklist"),
        ),
        d.canEdit && p.editable ? ib("plus", "Add care task", () => lineModal("task", null)) : null,
      ),
      folded("Care tasks", live(p.tasks), (rows) =>
        table(
          [
            {
              h: "Task",
              f: (r: TaskLine) => el("b", {}, r.what),
            },
            {
              h: "When and how often",
              f: (r) =>
                el(
                  "span",
                  {},
                  r.howOften,
                  // A times-a-week task the week can't make up any more: a flag only.
                  r.behind ? el("div", {}, chip("Behind this week", "warn")) : "",
                ),
            },
            { h: "Done so far", f: (r) => r.done },
            { h: "", f: (r) => rowBtns("task", r) },
          ],
          rows,
          undefined,
          "Care tasks",
        ),
      ),
      el(
        "div",
        { class: "grid2", style: "margin-top:12px" },
        el(
          "div",
          {},
          el("h3", { class: "h3s" }, "Precautions"),
          live(p.precautions).length
            ? el(
                "ul",
                { class: "plist2" },
                ...live(p.precautions).map((x) =>
                  el(
                    "li",
                    { class: x.archived ? "muted" : undefined },
                    x.what,
                    " ",
                    x.archived
                      ? el(
                          "span",
                          { class: "small" },
                          `(archived ${x.archived.on})`,
                          d.canEdit && p.editable
                            ? btn("Restore", "history", () => restoreLine(x.id), "btn sm ghost")
                            : "",
                        )
                      : d.canEdit && p.editable
                        ? ib("archive", "Archive this precaution", () => archiveLine(x.id, x.what))
                        : "",
                  ),
                ),
              )
            : el("p", { class: "muted small" }, "None."),
          d.canEdit && p.editable
            ? btn("Add precaution", "plus", () => lineModal("precaution", null), "btn sm ghost")
            : null,
        ),
        el(
          "div",
          {},
          el("h3", { class: "h3s" }, "Holds and extras"),
          d.holds.length
            ? el(
                "ul",
                { class: "plist2" },
                ...d.holds.map((h) =>
                  el(
                    "li",
                    {},
                    icon("stop"),
                    ` Hold ${h.from} – ${h.to ?? "now"} · ${h.reason}${h.paused.length ? ` · Paused: ${h.paused.join(", ")}` : ""}${h.ended ? ` · ${h.ended}` : ""}`,
                  ),
                ),
              )
            : el("p", { class: "muted small" }, "None."),
        ),
      ),
    );
  }

  /**
   * The nursing schedule card (brief 1E-25, mockup v85): each visit with how often, what it's
   * counted from, the next due date with Schedule beside it, and a pencil (a reason is asked).
   */
  function nursingCard(d: Tab): HTMLElement | null {
    const n = d.nursing;
    type Row = (typeof n.rows)[number];
    const dueChip = (r: Row) =>
      r.state === "as_needed"
        ? chip("As needed", "plain")
        : r.state === "paused"
          ? chip("Paused", "warn")
          : r.due
            ? chip(
                r.state === "overdue" ? `Overdue ${r.due}` : `Due ${r.due}`,
                r.state === "overdue" ? "bad" : r.state === "warn" ? "warn" : "plain",
              )
            : muted("–");
    const rows = table<Row>(
      [
        {
          h: "Visit",
          f: (r) =>
            el("span", {}, el("b", {}, r.name), el("div", { class: "small muted" }, r.from)),
        },
        { h: "How often", f: (r) => r.every },
        { h: "Due", f: dueChip },
        {
          h: "",
          f: (r) =>
            el(
              "span",
              { class: "rowbtns" },
              d.canEdit && r.state !== "as_needed"
                ? btn("Schedule", "calendar", () => goTab("visits"))
                : null,
              d.canEdit && r.kind === "recertification" && n.canRecertify
                ? btn("Recertify", "sync", () => recertModal(d))
                : null,
              d.canEdit ? ib("pencil", `Change ${r.name}`, () => nursingModal(d, r)) : null,
            ),
        },
      ],
      n.rows,
    );
    return el(
      "section",
      { class: "card", "aria-label": "Nursing schedule" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, "Nursing schedule"),
        d.canEdit ? btn("Add", "plus", () => nursingModal(d, null)) : null,
      ),
      rows,
      n.history.length
        ? el(
            "p",
            { class: "small muted", style: "margin:8px 0 0" },
            icon("history"),
            ` ${n.history[0]}`,
          )
        : null,
    );
  }

  function nursingModal(d: Tab, r: Tab["nursing"]["rows"][number] | null) {
    const name = field("Visit", r?.name ?? "", { placeholder: "e.g. Medication review" });
    const every = field("Every how many days", r?.everyDays ? String(r.everyDays) : "", {
      type: "number",
      placeholder: "Leave empty when it's as needed",
    });
    const needed = field("As needed for", r?.asNeeded ?? "", {
      placeholder: "e.g. When the aide or family calls",
    });
    const from = select(
      "Counted from",
      d.nursing.countedFrom.map((c) => [c.value, c.label] as const),
      r?.countedFrom ?? "start_of_care",
    );
    const next = field("Next due", r?.nextDue ?? "", { type: "date" });
    const why = field("Reason for the change", "", {
      placeholder: "e.g. Assessment recommends every 60 days",
    });
    const save = go("Save", async () => {
      const res = (await api<Tab>("PUT", `${base}/nursing/${r ? r.key : "new"}`, {
        name: name.input.value,
        everyDays: every.input.value || null,
        asNeeded: needed.input.value,
        countedFrom: from.input.value,
        nextDue: next.input.value || null,
        reason: why.input.value,
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      data = res.body;
      draw();
      toast("Nursing schedule saved");
    });
    const m = openModal({
      title: r ? `Change ${r.name}` : "Add to the nursing schedule",
      body: [form(name.el, every.el, needed.el, from.el, next.el, ...(r ? [why.el] : []))],
      actions: [save],
    });
  }

  // ---- Modals -----------------------------------------------------------------------------------------

  function holdModal(d: Tab, p: Plan) {
    const svcs = [...new Set(p.services.filter((s) => !s.archived).map((s) => s.service))];
    const why = select(
      "Why",
      d.holdReasons.map((r) => [r.value, r.value] as const),
      d.holdReasons[0]?.value ?? "",
    );
    const from = field("From", d.today, { type: "date" });
    const boxes = svcs.map((s) => {
      const b = el("input", { type: "checkbox", value: s, checked: true });
      return { b, el: el("label", {}, b, ` ${s}`) };
    });
    const save = go("Put on hold", async () => {
      const res = (await api<Tab>("POST", `${base}/hold`, {
        reason: why.input.value,
        startsOn: from.input.value,
        paused: boxes.filter((x) => x.b.checked).map((x) => x.b.value),
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(
        res,
        "Care on hold",
        `${boxes.filter((x) => x.b.checked).length} service${boxes.filter((x) => x.b.checked).length === 1 ? "" : "s"} paused · nothing is billed`,
      );
    });
    save.prepend(icon("stop"), " ");
    const m = openModal({
      title: "Put care on hold",
      sub: d.patient.name,
      body: [
        form(
          why.el,
          from.el,
          el(
            "fieldset",
            { class: "field" },
            el("legend", {}, "What stops"),
            el("div", { class: "checks" }, ...boxes.map((x) => x.el)),
          ),
        ),
      ],
      actions: [save],
    });
  }

  // ---- Edit and sign again (brief 1E-23, mockup v83 changeLine and verModal) ------------------------

  /**
   * The plan's change line, its own small card above it (apart from the activity feed): the
   * version in use, who signed it and when, the versions; or, while it's edited, the draft with
   * Sign and Cancel.
   */
  function changeLine(d: Tab, p: Plan): HTMLElement | null {
    const cur = p.versions.find((v) => v.current) ?? p.versions.at(-1);
    if (!cur) return null;
    const edit = p.editing;
    const sign = btn(
      `Sign version ${edit?.version ?? ""}`,
      "check",
      () =>
        void send(
          "POST",
          "/care-plan/sign",
          {},
          `Signed · version ${edit?.version ?? ""}`,
          `Version ${cur.version} is kept`,
        ),
      "btn sm pri",
    );
    sign.disabled = !p.canSign;
    return el(
      "section",
      { class: "card chgline", "aria-label": "Signed version" },
      el(
        "div",
        { class: "cardhead", style: "margin:0;align-items:center" },
        edit
          ? el(
              "div",
              {},
              el("b", {}, icon("pencil"), ` Editing care plan · version ${edit.version} draft`),
              el(
                "div",
                { class: "small muted" },
                p.signRefusal ?? `Version ${cur.version} stays in use until this is signed.`,
              ),
            )
          : el(
              "div",
              {},
              el("b", {}, `Care plan · version ${cur.version}`),
              el(
                "span",
                { class: "small muted" },
                ` · ${cur.version > 1 ? "updated" : "signed"} ${cur.when} by ${cur.by}`,
              ),
              p.versions.length > 1
                ? btn(
                    `${p.versions.length} versions`,
                    "history",
                    () => versionsModal(p),
                    "btn sm ghost",
                  )
                : "",
            ),
        d.canEdit
          ? edit
            ? el(
                "span",
                { class: "actions" },
                sign,
                btn(
                  "Cancel",
                  "x",
                  () =>
                    void send(
                      "POST",
                      "/care-plan/edit/cancel",
                      {},
                      "Not signed",
                      `Version ${cur.version} stays in use`,
                    ),
                  "btn sm",
                ),
              )
            : btn(
                "Edit",
                "pencil",
                () =>
                  void send(
                    "POST",
                    "/care-plan/edit",
                    {},
                    "Editing",
                    "Sign when done to make it the next version",
                  ),
                "btn sm",
              )
          : el("span"),
      ),
    );
  }

  /** Every signed version (mockup verModal): the one in use, and the rest to open read-only. */
  function versionsModal(p: Plan) {
    const rows = [...p.versions].reverse();
    openModal({
      title: "Care plan versions",
      sub: "Each signed version is kept. Open one to see it as it was signed.",
      body: [
        table(
          [
            { h: "Version", f: (v: Plan["versions"][number]) => el("b", {}, String(v.version)) },
            { h: "Signed", f: (v) => v.when },
            { h: "By", f: (v) => v.by },
            { h: "What changed", f: (v) => v.what },
            {
              h: "",
              f: (v) =>
                v.current
                  ? chip("In use", "ok")
                  : btn("Open", "eye", () => void openVersion(v.version), "btn sm ghost"),
            },
          ],
          rows,
          undefined,
          "Care plan versions",
        ),
      ],
      wide: true,
    });
  }

  /** One signed version, read-only, as it was signed. */
  async function openVersion(n: number) {
    const res = await api<{
      version: number;
      signed: { by: string; at: string };
      what: string;
      from: string;
      to: string;
      summary: string | null;
      services: { service: string; who: string | null; what: string }[];
      tasks: { what: string; howOften: string }[];
      precautions: string[];
    }>("GET", `${base}/care-plan/versions/${n}`);
    if (!res.ok) return toast("That version can't be opened", res.body.error);
    const v = res.body;
    openModal({
      title: `Care plan · version ${v.version}`,
      sub: `Read only, as signed · ${v.signed.by} · ${v.signed.at}`,
      body: [
        kv([
          ["Dates", `${v.from} – ${v.to}`],
          ["What changed", v.what],
          ...(v.summary ? ([["Summary", v.summary]] as [string, string][]) : []),
        ]),
        el("h3", { class: "h3s" }, "Services"),
        v.services.length
          ? table(
              [
                {
                  h: "Who",
                  f: (r: (typeof v.services)[number]) =>
                    `${r.service}${r.who ? ` · ${r.who}` : ""}`,
                },
                { h: "What and how often", f: (r) => r.what },
              ],
              v.services,
              undefined,
              "Services",
            )
          : muted("None."),
        el("h3", { class: "h3s" }, "Care tasks"),
        v.tasks.length
          ? table(
              [
                { h: "Task", f: (r: (typeof v.tasks)[number]) => r.what },
                { h: "When and how often", f: (r) => r.howOften },
              ],
              v.tasks,
              undefined,
              "Care tasks",
            )
          : muted("None."),
        el("h3", { class: "h3s" }, "Precautions"),
        v.precautions.length
          ? el("ul", { class: "plist2" }, ...v.precautions.map((x) => el("li", {}, x)))
          : muted("None."),
      ],
      wide: true,
    });
  }

  function datesModal(p: Plan) {
    const from = field("From", p.startsOn, { type: "date" });
    const to = field("To", p.endsOn, { type: "date" });
    const save = go("Save", async () => {
      const res = (await api<Tab>("PUT", `${base}/care-plan`, {
        startsOn: from.input.value,
        endsOn: to.input.value,
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(res, "Dates saved");
    });
    const m = openModal({
      title: "The plan's dates",
      body: [form(el("div", { class: "formgrid" }, from.el, to.el))],
      actions: [save],
    });
  }

  function recertModal(d: Tab) {
    const on = field("The new episode starts", d.nursing.recertifyOn ?? d.today, { type: "date" });
    const save = go("Recertify", async () => {
      const res = (await api<Tab>("POST", `${base}/recertify`, {
        startsOn: on.input.value,
      })) as Res;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(
        res,
        `Episode ${res.body.episodeNumber ?? ""} started`,
        "Linked to the one before · a new assessment and plan are due",
      );
    });
    const m = openModal({
      title: "Recertify",
      sub: "Starts a new episode with a new assessment and care plan. This one closes the day before.",
      body: [form(on.el)],
      actions: [save],
    });
  }

  // ---- The tab ------------------------------------------------------------------------------------------

  function draw() {
    const d = data;
    if (!d) return;
    const parts: Node[] = [];
    if (d.plan) {
      const line = changeLine(d, d.plan);
      if (line) parts.push(line);
      parts.push(planCard(d, d.plan));
    } else parts.push(noPlan(d));
    if (d.plan) {
      const n = nursingCard(d);
      if (n) parts.push(n);
    }
    host.replaceChildren(...parts);
  }

  await reload();
  return { reload };
}

// ---- The Authorizations card on Patient and payer (mockup authrow) -----------------------------------------

type Auths = PatientAuthorizations & { error?: string; raised?: number; moved?: string | null };

/** One authorization's row: service, number, dates, shape, the burn bar, used of total, due in. */
export function authRow(a: AuthorizationView, buttons: Node[]): HTMLElement {
  const pct = a.pct ?? 0;
  return el(
    "div",
    { class: `authrow${a.archived ? " arch" : ""}`, "data-auth": a.number },
    el(
      "div",
      { class: "top" },
      el("b", {}, a.service),
      el(
        "span",
        { class: "muted small" },
        // Corrected (brief 1E-23): when, the Changes list is on the pencil.
        a.changes.length ? icon("pencil") : "",
        a.changes.length ? ` Changed ${a.changes[0]?.when.slice(0, 5)} · ` : "",
        `${a.number} · ${a.dates} · ${a.shape}`,
      ),
      a.archived
        ? chip(`Archived ${a.archived.on}`, "plain")
        : el("span", { class: "rowbtns" }, ...buttons),
    ),
    a.of !== null
      ? el(
          "div",
          { class: `burn${a.low ? " low" : ""}`, role: "img", "aria-label": `${pct}% used` },
          el("i", { style: `width:${Math.min(100, pct)}%` }),
        )
      : null,
    el(
      "div",
      { class: "small" },
      a.words,
      // Scheduled visits count as planned against it (brief 1D-1).
      a.planned ? ` · ${a.plannedWords}` : "",
      " ",
      a.low ? chip("Low balance", "warn") : "",
      " ",
      a.over ? chip("Over the units", "bad") : "",
      " ",
      chip(a.due, a.dueLevel),
      a.overagePrivatePay
        ? el("span", { class: "muted" }, " · overage billed to the private payer")
        : "",
      a.billBy ? el("span", { class: "muted" }, ` · Bill by ${a.billBy.toLowerCase()}`) : "",
    ),
  );
}

/** Bill by on an authorization (brief 1E-35): the contract's unless it sets its own. */
function billBySelect(value: string) {
  return select(
    "Bill by",
    [["", "As the contract"] as const, ...BILL_BY.map((b) => [b, BILL_BY_LABEL[b]] as const)],
    value,
  );
}

/** Draws the Authorizations card into `host` (Patient and payer). Answers a reload. */
export async function authorizationsCard(
  host: HTMLElement,
  patientId: string,
  changed: () => void,
): Promise<{ reload: () => Promise<void> }> {
  const base = `/api/patients/${encodeURIComponent(patientId)}`;
  let data: Auths | null = null;
  let showArchived = false;

  async function reload() {
    const res = await api<Auths>("GET", `${base}/authorizations`);
    if (!res.ok) {
      host.replaceChildren();
      return;
    }
    data = res.body;
    draw();
  }
  async function took(res: { ok: boolean; body: Auths }, done: string, sub?: string) {
    if (!res.ok) return false;
    data = res.body;
    draw();
    toast(done, sub);
    changed();
    return true;
  }

  function addModal(d: Auths) {
    const svc = select(
      "Service",
      d.services.map((s) => [s.id, s.name] as const),
      d.services[0]?.id ?? "",
    );
    const number = field("Authorization or certificate number", "", { maxlength: "60" });
    const kind = select(
      "Shape",
      d.kinds.map((k) => [k.value, k.label] as const),
      "count",
    );
    const total = field("Total", "", { inputmode: "decimal" });
    const per = field("Each period", "", { inputmode: "decimal" });
    const period = select(
      "Period",
      d.periods.map((p) => [p.value, p.label] as const),
      "week",
    );
    const weeks = field("Weeks", "", { inputmode: "numeric" });
    const from = field("From", d.today, { type: "date" });
    const to = field("To", "", { type: "date" });
    const letter = filePick("The letter (optional)", d.files, patientId, "Upload the letter", {
      fileKind: d.letterKind,
    });
    const overage = el("input", { type: "checkbox", id: `ov-${patientId}` });
    // Brief 1E-35: an authorization can set its own Bill by; empty follows the contract.
    const billBy = billBySelect("");
    const shape = () => {
      const k = kind.input.value;
      const unit = d.services.find((s) => s.id === svc.input.value)?.unitWords ?? "units";
      total.el.hidden = k !== "count";
      per.el.hidden = k !== "frequency";
      period.el.hidden = k !== "frequency";
      weeks.el.hidden = k !== "frequency" || period.input.value !== "weeks";
      (total.el.querySelector("label") as HTMLElement).textContent = `Total ${unit}`;
      (per.el.querySelector("label") as HTMLElement).textContent =
        `${unit[0]?.toUpperCase()}${unit.slice(1)} each period`;
    };
    kind.input.addEventListener("change", shape);
    svc.input.addEventListener("change", shape);
    period.input.addEventListener("change", shape);
    shape();
    const save = go("Add authorization", async () => {
      const res = await api<Auths>("POST", `${base}/authorizations`, {
        serviceId: svc.input.value,
        number: number.input.value,
        kind: kind.input.value,
        total: total.input.value,
        perPeriod: per.input.value,
        period: period.input.value,
        periodWeeks: weeks.input.value,
        startsOn: from.input.value,
        endsOn: to.input.value,
        letterFileId: letter.input.value || undefined,
        overagePrivatePay: overage.checked,
        billBy: billBy.input.value,
      });
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(
        res,
        "Authorization added",
        res.body.moved
          ? `The episode moved on to ${res.body.moved}`
          : "The office is told when it runs low or nears its end",
      );
    });
    const m = openModal({
      title: "Add an authorization",
      sub: "Each authorization covers one service with a number and dates.",
      body: [
        form(
          el(
            "div",
            { class: "formgrid authform" },
            svc.el,
            number.el,
            kind.el,
            total.el,
            per.el,
            period.el,
            weeks.el,
            from.el,
            to.el,
            letter.el,
            billBy.el,
            el(
              "div",
              { class: "field wide" },
              el(
                "label",
                { for: overage.id },
                overage,
                " Units over it are billed to a private payer",
              ),
            ),
          ),
        ),
      ],
      actions: [save],
      wide: true,
    });
  }

  /**
   * The pencil (brief 1E-23, mockup authModal): what was typed wrong, corrected with a reason. The
   * change is kept in its history (the Changes list). A renewal or a new letter is a new one.
   */
  function changeModal(d: Auths, a: AuthorizationView) {
    const e = a.edit;
    const number = field("Number", a.number, { maxlength: "60" });
    const svc = select(
      "Service",
      d.services.map((s) => [s.id, s.name] as const),
      e.serviceId,
    );
    const from = field("From", e.from, { placeholder: "MM/DD/YYYY", inputmode: "numeric" });
    const to = field("To", e.to, { placeholder: "MM/DD/YYYY", inputmode: "numeric" });
    const kind = select(
      "Shape",
      d.kinds.map((k) => [k.value, k.label] as const),
      e.kind,
    );
    const total = field("Total", e.total, { inputmode: "decimal" });
    const per = field("How often", e.perPeriod, { inputmode: "decimal" });
    const period = select(
      "Period",
      d.periods.map((p) => [p.value, p.label] as const),
      e.period,
    );
    const weeks = field("Weeks", e.periodWeeks, { inputmode: "numeric" });
    const used = field(
      `Used before ${d.today.slice(5, 7)}/${d.today.slice(8, 10)}`,
      String(e.typedBefore),
      {
        inputmode: "decimal",
      },
    );
    const letter = filePick("Letter", d.files, patientId, "Upload the letter", {
      value: e.letterFileId,
      none: "No letter yet",
      fileKind: d.letterKind,
    });
    const overage = el("input", {
      type: "checkbox",
      id: `ovc-${a.id}`,
      checked: e.overagePrivatePay,
    });
    const billBy = billBySelect(e.billBy ?? "");
    const why = field("Reason for the change", "", {
      maxlength: "300",
      placeholder: "e.g. From date typed wrong",
    });
    const shape = () => {
      const k = kind.input.value;
      total.el.hidden = k === "continuous";
      per.el.hidden = k !== "frequency";
      period.el.hidden = k !== "frequency";
      weeks.el.hidden = k !== "frequency" || period.input.value !== "weeks";
    };
    kind.input.addEventListener("change", shape);
    period.input.addEventListener("change", shape);
    shape();
    const save = go("Save", async () => {
      // One date rule (brief 1E-23): a date that can't be read is refused before it's sent.
      if (!dayOf(from.input.value) || !dayOf(to.input.value))
        return refuse(m.note, `${DATE_REFUSED}. ${DATE_EXAMPLE}`);
      const res = await api<Auths>("PATCH", `${base}/authorizations/${a.id}`, {
        number: number.input.value,
        serviceId: svc.input.value,
        kind: kind.input.value,
        total: total.input.value,
        perPeriod: per.input.value,
        period: period.input.value,
        periodWeeks: weeks.input.value,
        startsOn: from.input.value,
        endsOn: to.input.value,
        letterFileId: letter.input.value || null,
        overagePrivatePay: overage.checked,
        billBy: billBy.input.value,
        usedBefore: used.input.value,
        reason: why.input.value,
      });
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      const now = res.body.authorizations.find((x) => x.id === a.id);
      await took(res, "Authorization saved", now?.changes[0]?.changed);
    });
    const m = openModal({
      title: `Change authorization · ${a.service}`,
      sub: "Fix what was typed wrong. A renewal or a new letter is a new authorization.",
      body: [
        form(
          el(
            "div",
            { class: "formgrid authform" },
            number.el,
            svc.el,
            from.el,
            to.el,
            kind.el,
            total.el,
            per.el,
            period.el,
            weeks.el,
            used.el,
            letter.el,
            billBy.el,
            el(
              "div",
              { class: "field wide" },
              el(
                "label",
                { for: overage.id },
                overage,
                " Units over it are billed to a private payer",
              ),
            ),
            el("div", { class: "wide" }, why.el),
          ),
        ),
        ...(a.changes.length
          ? [
              el("h3", { class: "h3s" }, "Changes"),
              table(
                [
                  { h: "When", f: (r: AuthorizationView["changes"][number]) => r.when },
                  { h: "Who", f: (r) => r.who },
                  { h: "Changed", f: (r) => el("b", {}, r.changed) },
                  { h: "Reason", f: (r) => r.reason },
                ],
                a.changes,
                undefined,
                "Changes",
              ),
            ]
          : []),
      ],
      actions: [save],
      wide: true,
    });
  }

  function usageModal(d: Auths, a: AuthorizationView) {
    const used = field(`${a.unitWords[0]?.toUpperCase()}${a.unitWords.slice(1)} used`, "", {
      inputmode: "decimal",
    });
    const on = field("On", d.today, { type: "date" });
    const note = field("Note (optional)", "", { maxlength: "300" });
    const save = go("Enter usage", async () => {
      const res = await api<Auths>("POST", `${base}/authorizations/${a.id}/usage`, {
        used: used.input.value,
        usedOn: on.input.value,
        note: note.input.value,
      });
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      const now = res.body.authorizations.find((x) => x.id === a.id);
      await took(
        res,
        "Usage entered",
        res.body.raised
          ? `Watch: ${now?.pct ?? ""}% used · an inbox item went to the office`
          : now?.words,
      );
    });
    const m = openModal({
      title: "Enter usage",
      sub: `${a.service} · ${a.number} · ${a.words}. Recorded visits count toward it already.`,
      body: [form(el("div", { class: "formgrid" }, used.el, on.el, note.el))],
      actions: [save],
    });
  }

  /**
   * Typed usage flagged as a possible double count (brief 1E-15): one line each with Keep, or
   * Archive with a reason.
   */
  function doubleCounts(d: Auths, a: AuthorizationView): HTMLElement[] {
    return a.doubleCounts.map((u) => {
      const keep = btn(
        "Keep",
        "check",
        async () => {
          const res = await api<Auths>("POST", `${base}/authorizations/${a.id}/usage/${u.id}/keep`);
          if (!res.ok) return toast("That wasn't saved", res.body.error);
          await took(res, "Usage kept", `${u.used} on ${u.on}`);
        },
        "btn sm",
      );
      const drop = btn("Archive", "archive", () => archiveUsage(a, u), "btn sm ghost");
      return el(
        "div",
        {
          class: "note",
          role: "status",
          style: "margin:4px 0 8px;border-left-color:var(--warn)",
          "data-double": u.id,
        },
        el("b", {}, u.words),
        el(
          "span",
          { class: "small" },
          ` · ${u.used} typed in for ${u.on}${u.note ? ` · ${u.note}` : ""}`,
        ),
        d.canEdit ? el("span", { class: "actions", style: "margin-top:6px" }, keep, drop) : null,
      );
    });
  }

  function archiveUsage(a: AuthorizationView, u: AuthorizationView["doubleCounts"][number]) {
    const why = area("Why is it archived?", "");
    const save = go("Archive", async () => {
      const res = await api<Auths>("POST", `${base}/authorizations/${a.id}/usage/${u.id}/archive`, {
        reason: why.input.value,
      });
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(res, "Usage archived", `${u.used} on ${u.on}`);
    });
    const m = openModal({
      title: "Archive this usage",
      sub: `${a.service} · ${a.number} · ${u.used} on ${u.on}. It no longer counts toward the authorization.`,
      body: [form(why.el)],
      actions: [save],
    });
  }

  function archive(a: AuthorizationView) {
    const why = area("Why is it archived?", "");
    const save = go("Archive", async () => {
      const res = await api<Auths>("POST", `${base}/authorizations/${a.id}/archive`, {
        reason: why.input.value,
      });
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      await took(res, "Authorization archived");
    });
    const m = openModal({
      title: "Archive this authorization",
      sub: `${a.service} · ${a.number}. It moves to Archived with the reason given.`,
      body: [form(why.el)],
      actions: [save],
    });
  }

  function draw() {
    const d = data;
    if (!d) return;
    if (!d.episode) {
      host.replaceChildren();
      return;
    }
    const nArch = d.authorizations.filter((a) => a.archived).length;
    const rows = d.authorizations.filter((a) => showArchived || !a.archived);
    const toggle = nArch
      ? btn(
          `${showArchived ? "Hide" : "Show"} archived (${nArch})`,
          "archive",
          () => {
            showArchived = !showArchived;
            draw();
          },
          "btn sm ghost",
        )
      : null;
    host.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Authorizations" },
        el(
          "div",
          { class: "cardhead" },
          el("h2", {}, "Authorizations"),
          el(
            "span",
            { class: "actions" },
            toggle,
            d.canEdit ? btn("Add", "plus", () => addModal(d)) : null,
          ),
        ),
        rows.length
          ? el(
              "div",
              {},
              ...rows.flatMap((a) => [
                authRow(
                  a,
                  [
                    a.letter
                      ? ib(
                          "eye",
                          "View the original letter",
                          () =>
                            void openViewer({
                              file: a.letter as { id: string; name: string },
                              document: "Authorization",
                            }),
                        )
                      : null,
                    d.canEdit && !a.archived
                      ? ib("pencil", "Change this authorization", () => changeModal(d, a))
                      : null,
                    d.canEdit && !a.archived
                      ? ib("plus", "Enter usage", () => usageModal(d, a))
                      : null,
                    d.canEdit && !a.archived
                      ? ib("archive", "Archive this authorization", () => archive(a))
                      : null,
                  ].filter((x): x is HTMLButtonElement => x !== null),
                ),
                ...doubleCounts(d, a),
              ]),
            )
          : el("p", { class: "muted" }, "No authorization yet."),
      ),
    );
  }

  await reload();
  return { reload };
}
